import { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowLeft,
  Boxes,
  ChevronLeft,
  ChevronRight,
  Download,
  FileText,
  History,
  Loader2,
  Package,
  Users,
  Wrench,
} from 'lucide-react';

import { itemsApi } from '@/services/itemsApi';
import { itemLedgerApi } from '@/services/itemLedgerApi';
import { branchApi } from '@/services/branchApi';
import type { Item, ItemLedgerFilters, ItemStockSnapshot, LedgerLocation } from '@/types';

/** Lucide icons and any other simple icon component alike. */
type IconComponent = React.ComponentType<{ className?: string }>;
import { formatCurrency } from '@/utils/currency';
import { getActiveTimezone } from '@/utils/timezone';

/**
 * Item transaction history for the standalone admin SPA.
 *
 * Deliberately self-contained: this app doesn't carry the shared list
 * primitives (PeriodFilter / ListPagination / useResponsivePagination) that the
 * main portal's version composes, so the filters and pager are plain controls
 * here. Both pages read the same API.
 */

type TabKey = 'overview' | 'stock' | 'history';

const TABS: { key: TabKey; label: string; icon: typeof FileText }[] = [
  { key: 'overview', label: 'Overview', icon: FileText },
  { key: 'stock', label: 'Stock', icon: Boxes },
  { key: 'history', label: 'History', icon: History },
];

const MOVEMENT_TYPE_OPTIONS = [
  { value: 'PURCHASE', label: 'Purchase' },
  { value: 'SALE', label: 'Sale' },
  { value: 'RETURN', label: 'Return / Credit Note' },
  { value: 'ADJUSTMENT', label: 'Stock Adjustment' },
  { value: 'OPENING_STOCK', label: 'Opening Stock' },
  { value: 'TRANSFER_IN', label: 'Transfer In' },
  { value: 'TRANSFER_OUT', label: 'Transfer Out' },
  { value: 'SERVICE_USE', label: 'Service Use' },
  { value: 'SERVICE_READY', label: 'Service Delivered' },
  { value: 'SERVICE_RESTORE', label: 'Service Restore' },
  { value: 'RESERVE', label: 'Reserved' },
  { value: 'UNRESERVE', label: 'Reservation Released' },
  { value: 'DAMAGE', label: 'Damage' },
];

function movementTypeColor(type: string): string {
  switch (type) {
    case 'PURCHASE':
    case 'TRANSFER_IN':
    case 'OPENING_STOCK':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'SALE':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'SERVICE_USE':
    case 'SERVICE_READY':
      return 'bg-purple-100 text-purple-800 border-purple-200';
    case 'RETURN':
    case 'SERVICE_RESTORE':
      return 'bg-teal-100 text-teal-800 border-teal-200';
    case 'ADJUSTMENT':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'TRANSFER':
    case 'TRANSFER_OUT':
      return 'bg-indigo-100 text-indigo-800 border-indigo-200';
    case 'DAMAGE':
      return 'bg-red-100 text-red-800 border-red-200';
    case 'RESERVE':
    case 'UNRESERVE':
      return 'bg-gray-100 text-gray-600 border-gray-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
}

const humanise = (value: string) =>
  value.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (l) => l.toUpperCase());

const formatDateTime = (value: string) =>
  new Date(value).toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: getActiveTimezone(),
  });

const money = (value: number | null) => (value === null || value === undefined ? '—' : formatCurrency(value));

const STAGE_STYLES: Record<string, string> = {
  DEDUCTED: 'bg-purple-100 text-purple-800',
  RESERVED: 'bg-amber-100 text-amber-800',
  PENDING: 'bg-gray-100 text-gray-700',
};

export default function ItemDetail() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = (searchParams.get('tab') as TabKey) || 'overview';
  const setActiveTab = (tab: TabKey) => {
    searchParams.set('tab', tab);
    setSearchParams(searchParams, { replace: true });
  };

  const [page, setPage] = useState(1);
  const [branchId, setBranchId] = useState('');
  const [location, setLocation] = useState<LedgerLocation | ''>('');
  const [movementType, setMovementType] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [exporting, setExporting] = useState(false);

  const ledgerFilters: ItemLedgerFilters = useMemo(
    () => ({
      branchId: branchId || undefined,
      location: location || undefined,
      movementType: movementType || undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
    }),
    [branchId, location, movementType, startDate, endDate]
  );

  const { data: item, isLoading: itemLoading } = useQuery({
    queryKey: ['item', id],
    queryFn: () => itemsApi.getItemById(id),
    enabled: !!id,
  });

  const { data: snapshot, isLoading: snapshotLoading } = useQuery({
    queryKey: ['item-stock-snapshot', id],
    queryFn: () => itemLedgerApi.getStockSnapshot(id),
    enabled: !!id && (activeTab === 'stock' || activeTab === 'history'),
    staleTime: 60_000,
  });

  const { data: ledger, isLoading: ledgerLoading } = useQuery({
    queryKey: ['item-ledger', id, ledgerFilters, page],
    queryFn: () => itemLedgerApi.getLedger(id, { ...ledgerFilters, page, limit: 25 }),
    enabled: !!id && activeTab === 'history',
    placeholderData: (prev) => prev,
    staleTime: 30_000,
  });

  const { data: branchData } = useQuery({
    queryKey: ['branches-for-item-ledger'],
    queryFn: () => branchApi.getAllBranches(undefined, 1, 100),
    staleTime: 5 * 60_000,
  });

  const unit = item?.itemUnit?.symbol || item?.itemUnit?.name || '';

  const handleExport = async (format: 'excel' | 'csv') => {
    if (!item) return;
    try {
      setExporting(true);
      if (format === 'csv') await itemLedgerApi.downloadCsv(id, item.itemCode, ledgerFilters);
      else await itemLedgerApi.downloadExcel(id, item.itemCode, ledgerFilters);
    } finally {
      setExporting(false);
    }
  };

  const resetPage = <T,>(setter: (value: T) => void) => (value: T) => {
    setter(value);
    setPage(1);
  };

  if (itemLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!item) {
    return (
      <div className="p-6">
        <p className="text-gray-600">Item not found.</p>
        <button onClick={() => navigate('/items')} className="mt-4 text-blue-600 hover:underline">
          Back to Items
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-blue-600 to-blue-500 rounded-xl px-5 py-4 text-white">
        <button
          onClick={() => navigate('/items')}
          className="inline-flex items-center gap-1 text-sm text-white/80 hover:text-white transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> Items
        </button>
        <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Package className="h-7 w-7" />
            <div>
              <h1 className="text-xl font-semibold">{item.itemName}</h1>
              <p className="text-sm text-white/80">
                {item.itemCode}
                {item.itemBrand?.name ? ` · ${item.itemBrand.name}` : ''}
                {item.itemCategory?.name ? ` · ${item.itemCategory.name}` : ''}
              </p>
            </div>
          </div>
          {snapshot && (
            <div className="text-right">
              <p className="text-xs uppercase tracking-wide text-white/70">On hand</p>
              <p className="text-2xl font-bold">
                {snapshot.totals.onHand}
                {unit && <span className="text-sm font-normal ml-1">{unit}</span>}
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === key
                ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow'
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && <OverviewTab item={item} />}

      {activeTab === 'stock' && <StockTab snapshot={snapshot} loading={snapshotLoading} unit={unit} />}

      {activeTab === 'history' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap gap-3 items-end">
            <label className="text-sm">
              <span className="block text-gray-500 mb-1">From</span>
              <input
                type="date"
                value={startDate}
                onChange={(e) => resetPage(setStartDate)(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </label>
            <label className="text-sm">
              <span className="block text-gray-500 mb-1">To</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => resetPage(setEndDate)(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </label>

            <select
              value={branchId}
              onChange={(e) => resetPage(setBranchId)(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">All branches</option>
              {branchData?.branches?.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </select>

            <select
              value={location}
              onChange={(e) => resetPage(setLocation)(e.target.value as LedgerLocation | '')}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">Branch + technician</option>
              <option value="BRANCH">Branch stock only</option>
              <option value="TECHNICIAN">Technician stock only</option>
            </select>

            <select
              value={movementType}
              onChange={(e) => resetPage(setMovementType)(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">All transaction types</option>
              {MOVEMENT_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            <div className="ml-auto flex gap-2">
              <button
                onClick={() => handleExport('excel')}
                disabled={exporting}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              >
                <Download className="h-4 w-4" /> Excel
              </button>
              <button
                onClick={() => handleExport('csv')}
                disabled={exporting}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              >
                <Download className="h-4 w-4" /> CSV
              </button>
            </div>
          </div>

          {ledger?.variance ? (
            <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
              <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
              <div className="text-sm text-amber-900">
                <p className="font-semibold">
                  Unexplained variance of {ledger.variance} {unit}
                </p>
                <p className="mt-1">
                  The recorded transactions don't fully account for the current stock — usually stock
                  that changed before transaction history was recorded.
                </p>
              </div>
            </div>
          ) : null}

          {ledger && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <SummaryCard label="Opening" value={`${ledger.openingQty} ${unit}`} />
              <SummaryCard label="Total In" value={`+${ledger.totals.qtyIn} ${unit}`} tone="green" />
              <SummaryCard label="Total Out" value={`-${ledger.totals.qtyOut} ${unit}`} tone="red" />
              <SummaryCard label="Closing" value={`${ledger.closingQty} ${unit}`} />
              <SummaryCard label="Current on hand" value={`${ledger.currentQty} ${unit}`} tone="blue" />
            </div>
          )}

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {ledgerLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
              </div>
            ) : !ledger || ledger.rows.length === 0 ? (
              <div className="py-16 text-center">
                <History className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No transactions found for this item.</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Date</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Transaction</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Document</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Party</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Location</th>
                        <th className="px-4 py-3 text-right font-semibold text-gray-700">In</th>
                        <th className="px-4 py-3 text-right font-semibold text-gray-700">Out</th>
                        <th className="px-4 py-3 text-right font-semibold text-gray-700">Balance</th>
                        <th className="px-4 py-3 text-right font-semibold text-gray-700">Rate</th>
                        <th className="px-4 py-3 text-right font-semibold text-gray-700">Value</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">By</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {ledger.rows.map((row) => (
                        <tr key={row.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 whitespace-nowrap text-gray-600">{formatDateTime(row.date)}</td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border ${movementTypeColor(row.movementType)}`}
                            >
                              {humanise(row.movementType)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {row.document ? (
                              <div>
                                <div className="text-gray-900">{row.document.number || '—'}</div>
                                <div className="text-xs text-gray-500">{row.document.kind}</div>
                              </div>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-gray-700">{row.party?.name || '—'}</td>
                          <td className="px-4 py-3">
                            <div className="text-gray-900">
                              {row.location === 'TECHNICIAN'
                                ? row.technician?.name || 'Technician'
                                : row.branch?.name || '—'}
                            </div>
                            {row.location === 'TECHNICIAN' && (
                              <div className="text-xs text-blue-600">Technician stock</div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right font-medium text-green-600">
                            {row.direction === 'IN' ? `+${row.delta}` : ''}
                          </td>
                          <td className="px-4 py-3 text-right font-medium text-red-600">
                            {row.direction === 'OUT' ? `${Math.abs(row.delta)}` : ''}
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-gray-900">
                            {row.balanceAfter === null ? (
                              <span className="text-gray-400" title="Reservation only — stock unchanged">
                                —
                              </span>
                            ) : (
                              row.balanceAfter
                            )}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-700">{money(row.rate)}</td>
                          <td className="px-4 py-3 text-right text-gray-700">{money(row.value)}</td>
                          <td className="px-4 py-3 text-gray-600">{row.user?.name || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
                  <p className="text-sm text-gray-700">
                    Page {ledger.pagination.page} of {ledger.pagination.totalPages} ·{' '}
                    {ledger.pagination.total} transactions
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page <= 1}
                      className="inline-flex items-center px-3 py-2 text-sm border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50"
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" /> Previous
                    </button>
                    <button
                      onClick={() => setPage((p) => p + 1)}
                      disabled={page >= ledger.pagination.totalPages}
                      className="inline-flex items-center px-3 py-2 text-sm border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50"
                    >
                      Next <ChevronRight className="h-4 w-4 ml-1" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCard({
  label,
  value,
  tone = 'gray',
}: {
  label: string;
  value: string;
  tone?: 'gray' | 'green' | 'red' | 'blue';
}) {
  const tones = {
    gray: 'text-gray-900',
    green: 'text-green-600',
    red: 'text-red-600',
    blue: 'text-blue-600',
  };
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
      <p className={`text-lg font-bold mt-1 ${tones[tone]}`}>{value}</p>
    </div>
  );
}

function OverviewTab({ item }: { item: Item }) {
  return (
    <div className="space-y-4">
      <Panel title="Basic Information" icon={FileText}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
          <Field label="Item Name" value={item.itemName} />
          <Field label="Item Code" value={item.itemCode} />
          {item.modelVariant && <Field label="Model Variant" value={item.modelVariant} />}
          {item.barcode && <Field label="Barcode" value={item.barcode} />}
          <Field label="Category" value={item.itemCategory?.name} />
          <Field label="Brand" value={item.itemBrand?.name} />
          <Field label="Model" value={item.itemModel?.name} />
          <Field label="HSN Code" value={item.hsnCode} />
          <Field label="Purchase Price" value={formatCurrency(item.purchasePrice)} />
          <Field label="Sales Price" value={formatCurrency(item.salesPrice)} />
          <Field
            label="GST Rate"
            value={item.itemGSTRate ? `${item.itemGSTRate.rate}% - ${item.itemGSTRate.name}` : undefined}
          />
          <Field
            label="Unit"
            value={
              item.itemUnit
                ? item.itemUnit.symbol
                  ? `${item.itemUnit.name} (${item.itemUnit.symbol})`
                  : item.itemUnit.name
                : undefined
            }
          />
          {item.description && (
            <div className="md:col-span-2">
              <Field label="Description" value={item.description} />
            </div>
          )}
        </div>
      </Panel>

      <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500">Status</p>
          <span
            className={`mt-2 inline-block px-3 py-1 rounded-full text-sm font-medium ${
              item.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}
          >
            {item.isActive ? 'Active' : 'Inactive'}
          </span>
        </div>
        <div className="text-right">
          <p className="text-sm font-medium text-gray-500">Created</p>
          <p className="text-gray-900 mt-1">
            {new Date(item.createdAt).toLocaleDateString('en-IN', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              timeZone: getActiveTimezone(),
            })}
          </p>
        </div>
      </div>
    </div>
  );
}

function StockTab({
  snapshot,
  loading,
  unit,
}: {
  snapshot?: ItemStockSnapshot;
  loading: boolean;
  unit: string;
}) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!snapshot) return null;
  const { totals } = snapshot;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryCard label="On hand" value={`${totals.onHand} ${unit}`} tone="blue" />
        <SummaryCard label="Reserved" value={`${totals.reserved} ${unit}`} />
        <SummaryCard label="Available" value={`${totals.available} ${unit}`} tone="green" />
        <SummaryCard label="With technicians" value={`${totals.technicianStock} ${unit}`} />
      </div>

      <Panel title="Stock by branch" icon={Boxes}>
        {snapshot.byBranch.length === 0 ? (
          <EmptyRow text="Not stocked in any branch." />
        ) : (
          <SimpleTable
            head={['Branch', 'On hand', 'Reserved', 'Available', 'Min / Reorder']}
            rows={snapshot.byBranch.map((b) => [
              <div key="b">
                <div className="font-medium text-gray-900">{b.branchName}</div>
                <div className="text-xs text-gray-500">{b.branchCode}</div>
              </div>,
              `${b.stockQuantity} ${unit}`,
              `${b.reservedQuantity} ${unit}`,
              <span
                key="a"
                className={
                  b.availableQuantity <= (b.minStockLevel || 0)
                    ? 'text-red-600 font-semibold'
                    : 'text-green-600 font-semibold'
                }
              >
                {b.availableQuantity} {unit}
              </span>,
              `${b.minStockLevel ?? '—'} / ${b.reorderLevel ?? '—'}`,
            ])}
          />
        )}
      </Panel>

      <Panel title="Stock held by technicians" icon={Users}>
        {snapshot.byTechnician.length === 0 ? (
          <EmptyRow text="No technician is holding this item." />
        ) : (
          <SimpleTable
            head={['Technician', 'Branch', 'On hand', 'Reserved', 'Available']}
            rows={snapshot.byTechnician.map((t) => [
              t.technicianName,
              t.branchName || '—',
              `${t.stockQuantity} ${unit}`,
              `${t.reservedQuantity} ${unit}`,
              `${t.availableQuantity} ${unit}`,
            ])}
          />
        )}
      </Panel>

      <Panel title="Committed to open services" icon={Wrench}>
        {snapshot.inOpenServices.length === 0 ? (
          <EmptyRow text="Not used in any in-progress service." />
        ) : (
          <SimpleTable
            head={['Ticket', 'Customer', 'Service status', 'Qty', 'Stock stage']}
            rows={snapshot.inOpenServices.map((s) => [
              <div key="t">
                <div className="font-medium text-gray-900">{s.ticketNumber}</div>
                <div className="text-xs text-gray-500">{s.branchName || ''}</div>
              </div>,
              s.customerName || '—',
              humanise(s.serviceStatus),
              `${s.quantity} ${unit}`,
              <span key="s" className={`px-2 py-0.5 rounded-full text-xs font-medium ${STAGE_STYLES[s.stage]}`}>
                {s.stage === 'DEDUCTED' ? 'Deducted' : s.stage === 'RESERVED' ? 'Reserved' : 'Awaiting approval'}
              </span>,
            ])}
          />
        )}
      </Panel>
    </div>
  );
}

function Field({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <p className="text-sm font-medium text-gray-500">{label}</p>
      <p className="text-gray-900 mt-1">{value || 'N/A'}</p>
    </div>
  );
}

function Panel({ title, icon: Icon, children }: { title: string; icon: IconComponent; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
        <Icon className="h-5 w-5 text-blue-600" />
        <h3 className="font-semibold text-gray-900">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function SimpleTable({ head, rows }: { head: string[]; rows: React.ReactNode[][] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            {head.map((h) => (
              <th key={h} className="px-4 py-2 text-left font-semibold text-gray-700">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((cells, i) => (
            <tr key={i} className="hover:bg-gray-50">
              {cells.map((cell, j) => (
                <td key={j} className="px-4 py-3 text-gray-700">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EmptyRow({ text }: { text: string }) {
  return <p className="px-4 py-6 text-sm text-gray-500">{text}</p>;
}
